import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCut as ServiceIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import servicesService from '../../services/servicesService';
import placeServicesService from '../../services/placeServicesService';
import useSnackbar from '../../hooks/useSnackbar';
import { handleDeleteOperation } from '../../utils/apiErrorHandler';

/**
 * PlaceServicesManager - Gerenciador de Serviços do Local
 * 
 * Permite adicionar e remover serviços oferecidos em um local específico.
 * 
 * @param {Boolean} open - Se o diálogo está aberto
 * @param {Function} onClose - Callback ao fechar
 * @param {Object} place - Local sendo gerenciado
 * @param {Function} onUpdate - Callback após atualização (para recarregar dados)
 */
export default function PlaceServicesManager({ open, onClose, place, onUpdate }) {
  const [allServices, setAllServices] = useState([]);
  const [placeServices, setPlaceServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  // Carregar todos os serviços disponíveis
  useEffect(() => {
    if (open) {
      loadAllServices();
      loadPlaceServices();
    }
  }, [open, place?.id]);

  const loadAllServices = async () => {
    try {
      setLoadingServices(true);
      const services = await servicesService.getAll();
      console.log('[PlaceServicesManager] Serviços carregados:', services);
      setAllServices(services || []);
    } catch (error) {
      console.error('[PlaceServicesManager] Erro ao carregar serviços:', error);
      showError('Erro ao carregar serviços: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingServices(false);
    }
  };

  const loadPlaceServices = async () => {
    if (!place?.id) return;
    
    try {
      setLoading(true);
      const placeServs = await placeServicesService.getByPlaceId(place.id);
      console.log('[PlaceServicesManager] Serviços do local carregados:', placeServs);
      setPlaceServices(placeServs || []);
    } catch (error) {
      console.error('Erro ao carregar serviços do local:', error);
      setPlaceServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!selectedServiceId || !place?.id) return;

    // Verificar se o serviço já está adicionado (comparar GUIDs como strings)
    const alreadyAdded = placeServices.some(ps => ps.serviceId === selectedServiceId);
    if (alreadyAdded) {
      showError('Este serviço já está adicionado a este local');
      return;
    }

    try {
      setLoading(true);
      await placeServicesService.create(place.id, {
        serviceId: selectedServiceId, // Manter como GUID string
      });
      
      showSuccess('Serviço adicionado com sucesso!');
      setSelectedServiceId('');
      await loadPlaceServices();
      onUpdate?.();
    } catch (error) {
      showError('Erro ao adicionar serviço: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveService = async (serviceId) => {
    if (!window.confirm('Tem certeza que deseja remover este serviço do local?')) {
      return;
    }

    try {
      setLoading(true);
      
      // ✅ Usar handleDeleteOperation para tratar respostas corretamente
      const result = await handleDeleteOperation(
        (params) => placeServicesService.delete(params.placeId, params.serviceId),
        { placeId: place.id, serviceId }
      );

      if (result.success) {
        // Sucesso (HTTP 204)
        showSuccess('Serviço removido com sucesso!');
        await loadPlaceServices();
        onUpdate?.();
      } else {
        // Erro (400, 404, 500, etc)
        const errorMsg = result.error.message;
        showError(errorMsg);
      }
    } catch (error) {
      // Fallback para erros inesperados
      showError('Erro ao remover serviço: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedServiceId('');
    onClose();
  };

  // Filtrar serviços disponíveis (que ainda não foram adicionados)
  const availableServices = allServices.filter(
    service => !placeServices.some(ps => ps.serviceId === service.id)
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ServiceIcon />
          <Typography variant="h6">
            Gerenciar Serviços - {place?.name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Adicionar Novo Serviço */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Adicionar Serviço:
          </Typography>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              select
              fullWidth
              label="Selecione um serviço"
              value={selectedServiceId}
              onChange={(e) => setSelectedServiceId(e.target.value)}
              disabled={loading || loadingServices}
              size="small"
            >
              {loadingServices ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Carregando...
                </MenuItem>
              ) : availableServices.length === 0 ? (
                <MenuItem disabled>Todos os serviços já foram adicionados</MenuItem>
              ) : (
                availableServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name}
                  </MenuItem>
                ))
              )}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddService}
              disabled={!selectedServiceId || loading}
            >
              Adicionar
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Lista de Serviços do Local */}
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Serviços Oferecidos ({placeServices.length}):
        </Typography>

        {loading && placeServices.length === 0 ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : placeServices.length === 0 ? (
          <Alert severity="info">
            Nenhum serviço configurado para este local. Adicione serviços usando o campo acima.
          </Alert>
        ) : (
          <List>
            {placeServices.map((placeService) => (
              <ListItem
                key={placeService.id}
                divider
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ServiceIcon sx={{ mr: 2, color: 'primary.main' }} />
                <ListItemText
                  primary={placeService.serviceName || 'Serviço'}
                  secondary={
                    <>
                      {placeService.serviceDescription && (
                        <Typography variant="body2" color="text.secondary">
                          {placeService.serviceDescription}
                        </Typography>
                      )}
                      {placeService.serviceDuration && (
                        <Chip
                          label={`${placeService.serviceDuration} min`}
                          size="small"
                          sx={{ mt: 0.5, mr: 0.5 }}
                        />
                      )}
                      {placeService.servicePrice && (
                        <Chip
                          label={`R$ ${placeService.servicePrice.toFixed(2)}`}
                          size="small"
                          color="success"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => handleRemoveService(placeService.serviceId)}
                    disabled={loading}
                    title="Remover serviço"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Dica:</strong> Depois de adicionar serviços, você pode configurar formas de pagamento 
            específicas para cada serviço usando o gerenciador de pagamentos.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
